import React, { useMemo, useRef, useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SimpleGrid,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useDisclosure,
  HStack,
} from '@chakra-ui/react'
import { useMutation } from '@tanstack/react-query'
import { FiEye } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { ProgressModal, type ProgressStatus } from '@/components/common/ProgressModal'
import {
  findEntriesWithoutPrefix,
  parseMultilineEntries,
} from '@/components/common/triggerInputHelpers'
import { useApiError } from '@/hooks/useApiError'
import { useToast } from '@/hooks/useToast'
import { settlementService } from '@/services/admin/settlementService'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import { buildContractChainOptions } from './contractChainOptions'

interface SettlementPreviewResult {
  total: number
  queued: number
  skipped: number
}

export const SettlementPreviewTrigger: React.FC = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const { handleError } = useApiError()
  const { currentEnv } = useNetworkEnvironment()
  const bgColor = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const { isOpen: isProgressOpen, onOpen: onProgressOpen, onClose: onProgressClose } = useDisclosure()
  const requestedTotalRef = useRef(0)

  const [orderIds, setOrderIds] = useState('')
  const [chainId, setChainId] = useState('')
  const [limit, setLimit] = useState(10)
  const [orderIdsError, setOrderIdsError] = useState('')
  const [results, setResults] = useState<SettlementPreviewResult | null>(null)
  const [progressStatus, setProgressStatus] = useState<ProgressStatus>('idle')
  const [progressCurrent, setProgressCurrent] = useState(0)
  const [progressTotal, setProgressTotal] = useState(0)
  const [progressMessage, setProgressMessage] = useState('')
  const chainOptions = useMemo(
    () => buildContractChainOptions(currentEnv),
    [currentEnv]
  )

  const executePreview = async () => {
    const orderIdList = parseMultilineEntries(orderIds)

    const invalidIds = findEntriesWithoutPrefix(orderIdList, 'ord_')
    if (invalidIds.length > 0) {
      throw new Error(`Invalid order IDs: ${invalidIds.join(', ')}`)
    }

    const params: { orderIds?: string[]; chainId?: string; limit?: number } = {}
    if (orderIdList.length > 0) {
      params.orderIds = orderIdList
    } else {
      params.limit = limit
    }

    if (chainId) {
      params.chainId = chainId
    }

    const totalTarget = orderIdList.length > 0 ? orderIdList.length : limit
    requestedTotalRef.current = totalTarget
    setProgressTotal(totalTarget)
    setProgressCurrent(0)
    setProgressMessage(t('monitoring.settlementPreview.progress.preparing'))
    setProgressStatus('processing')
    onProgressOpen()

    const response = await settlementService.previewSettlements(params, currentEnv)
    if (!response.success) {
      throw new Error(response.error || t('monitoring.settlementPreview.error.triggerFailed'))
    }
    return response
  }

  const mutation = useMutation({
    mutationFn: executePreview,
    onSuccess: (response) => {
      const total = Number.isFinite(response.totalRequested) && response.totalRequested > 0
        ? response.totalRequested
        : requestedTotalRef.current || limit
      const queued = Number.isFinite(response.queuedCount) ? response.queuedCount : 0
      const skipped = Math.max(0, total - queued)
      const resultPayload: SettlementPreviewResult = {
        total,
        queued,
        skipped,
      }

      setResults(resultPayload)
      setProgressTotal(total)
      setProgressCurrent(total)
      setProgressStatus('completed')
      setProgressMessage(t('monitoring.settlementPreview.progress.completed'))

      const toastMethod = queued > 0 ? toast.success : toast.info
      toastMethod({
        title: t('monitoring.settlementPreview.success.title'),
        description: t('monitoring.settlementPreview.success.description', {
          queued,
          skipped,
          total,
        }),
        duration: 5000,
        isClosable: true,
        id: 'settlement-preview-success',
      })
    },
    onError: (error: any) => {
      setProgressStatus('failed')
      const errorMessage = error.response?.data?.error || error.message || t('monitoring.settlementPreview.error.triggerFailed')
      setProgressMessage(errorMessage)
      handleError(error, t('monitoring.settlementPreview.error.context'))
    },
  })

  const handleOrderIdsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setOrderIds(value)

    if (value) {
      const orderIdList = parseMultilineEntries(value)
      const invalidIds = findEntriesWithoutPrefix(orderIdList, 'ord_')
      if (invalidIds.length > 0) {
        setOrderIdsError(`Invalid order IDs: ${invalidIds.slice(0, 3).join(', ')}`)
      } else {
        setOrderIdsError('')
      }
    } else {
      setOrderIdsError('')
    }
  }

  const handleSubmit = () => {
    if (orderIdsError) {
      return
    }
    mutation.mutate()
  }

  const getStatColor = (value: number, type: 'queued' | 'skipped') => {
    if (value === 0) return 'gray'
    return type === 'queued' ? 'green' : 'yellow'
  }

  return (
    <VStack spacing={6} align="stretch">
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        {t('monitoring.settlementPreview.description')}
      </Alert>

      <Box>
        <VStack spacing={4} align="stretch">
          <FormControl isInvalid={!!orderIdsError}>
            <FormLabel>{t('monitoring.settlementPreview.fields.orderIds.label')}</FormLabel>
            <Textarea
              placeholder={t('monitoring.settlementPreview.fields.orderIds.placeholder')}
              value={orderIds}
              onChange={handleOrderIdsChange}
              rows={4}
              isDisabled={mutation.isPending}
            />
            {!orderIdsError && (
              <FormHelperText>
                {t('monitoring.settlementPreview.fields.orderIds.helper')}
              </FormHelperText>
            )}
            <FormErrorMessage>{orderIdsError}</FormErrorMessage>
          </FormControl>

          <FormControl>
            <FormLabel>{t('monitoring.settlementPreview.fields.chainId.label')}</FormLabel>
            <DropdownSelect
              placeholder={t('monitoring.settlementPreview.fields.chainId.placeholder')}
              emptyOptionLabel={t('monitoring.settlementPreview.fields.chainId.placeholder')}
              value={chainId}
              options={chainOptions}
              onChange={setChainId}
              ariaLabel={t('monitoring.settlementPreview.fields.chainId.label')}
              isDisabled={mutation.isPending}
            />
            <FormHelperText>
              {t('monitoring.settlementPreview.fields.chainId.helper')}
            </FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel>{t('monitoring.settlementPreview.fields.limit.label')}</FormLabel>
            <NumberInput
              value={limit}
              onChange={(_, value) => setLimit(value || 10)}
              min={1}
              max={100}
              isDisabled={mutation.isPending || orderIds.trim().length > 0}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <FormHelperText>
              {orderIds.trim().length > 0
                ? t('monitoring.settlementPreview.fields.limit.disabled')
                : t('monitoring.settlementPreview.fields.limit.helper')}
            </FormHelperText>
          </FormControl>

          <Button
            colorScheme="blue"
            leftIcon={<FiEye />}
            onClick={handleSubmit}
            isLoading={mutation.isPending}
            loadingText={t('monitoring.settlementPreview.button.loading')}
            isDisabled={mutation.isPending || !!orderIdsError}
          >
            {t('monitoring.settlementPreview.button.submit')}
          </Button>

          {mutation.isPending && (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle>{t('monitoring.settlementPreview.processing.title')}</AlertTitle>
                <AlertDescription>
                  <Text fontSize="sm">
                    {t('monitoring.settlementPreview.processing.description')}
                  </Text>
                  <Text fontSize="sm" mt={1}>
                    {t('monitoring.settlementPreview.processing.detail')}
                  </Text>
                </AlertDescription>
              </Box>
            </Alert>
          )}
        </VStack>
      </Box>

      {results && (
        <Box
          bg={bgColor}
          p={6}
          borderRadius="md"
          borderWidth={1}
          borderColor={borderColor}
        >
          <VStack spacing={4} align="stretch">
            <Box fontWeight="semibold" fontSize="lg">
              {t('monitoring.settlementPreview.results.title')}
            </Box>
            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
              <Stat>
                <StatLabel>
                  {t('monitoring.settlementPreview.results.total')}
                </StatLabel>
                <StatNumber>{results.total}</StatNumber>
              </Stat>
              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <span>{t('monitoring.settlementPreview.results.queued')}</span>
                    <Badge colorScheme={getStatColor(results.queued, 'queued')} size="sm">
                      {results.queued > 0 ? 'Queued' : '0'}
                    </Badge>
                  </HStack>
                </StatLabel>
                <StatNumber color={`${getStatColor(results.queued, 'queued')}.500`}>
                  {results.queued}
                </StatNumber>
                {results.total > 0 && (
                  <StatHelpText>
                    {((results.queued / results.total) * 100).toFixed(0)}%
                  </StatHelpText>
                )}
              </Stat>
              <Stat>
                <StatLabel>
                  <HStack spacing={1}>
                    <span>{t('monitoring.settlementPreview.results.skipped')}</span>
                    <Badge colorScheme={getStatColor(results.skipped, 'skipped')} size="sm">
                      {results.skipped > 0 ? 'Skipped' : '0'}
                    </Badge>
                  </HStack>
                </StatLabel>
                <StatNumber color={`${getStatColor(results.skipped, 'skipped')}.500`}>
                  {results.skipped}
                </StatNumber>
                {results.total > 0 && (
                  <StatHelpText>
                    {((results.skipped / results.total) * 100).toFixed(0)}%
                  </StatHelpText>
                )}
              </Stat>
            </SimpleGrid>
          </VStack>
        </Box>
      )}

      <ProgressModal
        isOpen={isProgressOpen}
        onClose={onProgressClose}
        title={t('monitoring.settlementPreview.progress.title')}
        total={progressTotal}
        current={progressCurrent}
        status={progressStatus}
        message={progressMessage}
        results={results ? {
          queued: results.queued,
          alreadyQueued: 0,
          failed: 0,
          skipped: results.skipped,
        } : undefined}
      />
    </VStack>
  )
}
